import { toLower } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { getCduUserLogon } from '@gef-ui/cdu-user/selectors';
import { resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';

import { campaignStates } from 'constants/campaigns';
import { USER_ROLES } from 'constants/general';
import collapsibleTable from 'features/common/collapsibleTable';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { saveFile } from 'features/common/fileSave/actions';
import contacts from 'features/views/contacts';
import { dialogs } from 'features/views/contacts/constants';
import user from 'features/views/user';
import { getRoutePath, trimFormData } from 'utils';
import history from 'utils/history';

import { CONTACTS_FORM_NAME, FIELDS } from '../contacts/form';
import { getContextUser } from '../userContext/selectors';
import * as types from './actionTypes';
import { COLLAPSIBLE_TABLE_NAME, REPORT_NOT_FOUND_DIALOG } from './constants';
import { getSort } from './selectors';
import { queryParamsFactory } from './utils';

const withProduction = true; // use it when only you need BE send you prodBp and ProdJp
const loadContactList = contacts.actions.loadContacts('list');
const sortContactList = contacts.actions.sortContacts('list');
const reset = createAction(types.RESET);
const selectContact = createAction(types.SELECT_CONTACT, (id) => ({ id }));
const changeSort = createAction(types.CHANGE_SORT, (sort) => ({ sort }));

const submitForm =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const data = await getFormData(CONTACTS_FORM_NAME);
		const formData = trimFormData(data);
		// true because there's need for prodBp and prodJp values
		const calculateBruttoProduction = true;
		const callGtmFilter = true;
		dispatch(dialog.actions.close(dialogs.TOO_MANY_CONTACTS_FOR_PRINT));
		dispatch(collapsibleTable.actions.removeOpenedRows(COLLAPSIBLE_TABLE_NAME));
		dispatch(actions.reset());
		dispatch(
			actions.loadContactList(
				{
					...formData,
					campaignStateIds: [campaignStates.RUNNING],
				},
				['campaign.campaignName', 'orderingClientName'],
				withProduction,
				calculateBruttoProduction,
				callGtmFilter,
			),
		);
	};

const resetContactList =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(contacts.actions.resetContacts('list'));
		dispatch(contacts.actions.clearFetchState('list'));
		sendControlNext(CONTACTS_FORM_NAME, resetForm());
		dispatch(collapsibleTable.actions.removeOpenedRows(COLLAPSIBLE_TABLE_NAME));
		dispatch(actions.reset());
		dispatch(actions.startupContacts());
	};

const sortContacts = (sort) => (dispatch) => {
	dispatch(actions.changeSort(sort));
	dispatch(actions.sortContactList(sort));
};

const startupContacts =
	() =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		await dispatch(user.actions.fetchAdditionalUserInfo());

		const state = getState();
		const role = user.selectors.getCurrentRole(state);
		const contextUser = getContextUser(state);
		const userLogin = getCduUserLogon(state);

		const searchParams = new URLSearchParams(window.location.search);
		const searchParamsArr = Array.from(searchParams.keys());

		if (searchParamsArr.length) {
			searchParamsArr.forEach((key) => {
				const value = searchParams.get(key);
				// prefilling allowed only for campaignId and contactState for now
				if (value) {
					if (key === 'campaignId') {
						sendControlNext(
							CONTACTS_FORM_NAME,
							setValue({ fieldName: FIELDS.campaign, value: Number(value) }),
						);
					}

					if (key === 'contactState') {
						sendControlNext(CONTACTS_FORM_NAME, setValue({ fieldName: FIELDS.contactState, value }));
					}

					if (key === 'prefillToSeller') {
						// prefilling orgStructure to selected subordinate orgStructure values
						sendControlNext(
							CONTACTS_FORM_NAME,
							setValue({ fieldName: FIELDS.channel, value: contextUser?.channelId }),
						);
						sendControlNext(
							CONTACTS_FORM_NAME,
							setValue({ fieldName: FIELDS.region, value: contextUser?.regionId }),
						);
						sendControlNext(
							CONTACTS_FORM_NAME,
							setValue({ fieldName: FIELDS.agency, value: contextUser?.agencyId }),
						);
						sendControlNext(
							CONTACTS_FORM_NAME,
							setValue({ fieldName: FIELDS.manager, value: toLower(userLogin) }),
						);
						sendControlNext(
							CONTACTS_FORM_NAME,
							setValue({ fieldName: FIELDS.seller, value: contextUser?.login }),
						);
					}
				}
			});

			history.replace(getRoutePath('contacts'));
		}

		if (!role) return;

		const data = await getFormData(CONTACTS_FORM_NAME);
		const trimmedFormData = trimFormData(data);

		const userStructure = user.selectors.getRoleStructure(state);
		const { channelId, regionId, agencyId, managerLogin, sellerLogin, ppzLogin } = userStructure;

		const defaultFormData = {
			...trimmedFormData,
			channel: channelId,
			region: regionId,
			agency: agencyId,
			manager: managerLogin,
			seller: sellerLogin,
			ppz: ppzLogin,
		};

		if (role === USER_ROLES.SELLER || role === USER_ROLES.PPZ) {
			// true because there's need for prodBp and prodJp values
			const calculateBruttoProduction = true;
			dispatch(
				actions.loadContactList(
					{
						...defaultFormData,
						campaignStateIds: [campaignStates.RUNNING],
					},
					getSort(state),
					withProduction,
					calculateBruttoProduction,
				),
			);
		}

		if (role === USER_ROLES.GROUP_MANAGER && !isNilOrEmpty(searchParamsArr)) {
			const finalFormData = searchParams.get('prefillToSeller')
				? {
						...trimmedFormData,
						channel: contextUser?.channelId,
						region: contextUser?.regionId,
						agency: contextUser?.agencyId,
						manager: toLower(userLogin),
						seller: contextUser?.login,
				  }
				: defaultFormData;

			// true because there's need for prodBp and prodJp values
			const calculateBruttoProduction = true;

			dispatch(
				actions.loadContactList(
					{
						...finalFormData,
						campaignStateIds: [campaignStates.RUNNING],
					},
					getSort(state),
					withProduction,
					calculateBruttoProduction,
				),
			);
		}
	};

const reportCampaign =
	() =>
	async (dispatch, getState, { getFormData }) => {
		dispatch(dialog.actions.close(REPORT_NOT_FOUND_DIALOG));
		const state = getState();
		const userRole = user.selectors.getCurrentRole(state);
		const data = await getFormData(CONTACTS_FORM_NAME);
		const formData = trimFormData(data);

		const response = await dispatch(
			fetch.actions.getBinary(fetch.constants.urls.campaignReport, {
				queryParams: queryParamsFactory(formData, userRole),
			}),
		);

		if (!response.body) {
			return dispatch(dialog.actions.open(REPORT_NOT_FOUND_DIALOG));
		}

		saveFile({ response });
	};

export const actions = {
	changeSort,
	loadContactList,
	reportCampaign,
	reset,
	resetContactList,
	selectContact,
	sortContactList,
	sortContacts,
	startupContacts,
	submitForm,
};
